import { RSAA } from 'redux-api-middleware';
import { buildRenterPaymentsUrlV2, checkStatus, GET, POST } from '@common/paymentsUtils/api';
import {
  REQUEST_CANCEL_SCHEDULED_PAYMENT,
  REQUEST_CREATE_MANUAL_PAYMENT,
  REQUEST_DELETE_PAYMENT_METHOD,
  REQUEST_GET_FEATURE_PERMISSIONS,
  REQUEST_INITIATE_PAYMENT,
  REQUEST_MARK_ACKNOWLEDGED,
  REQUEST_MOVE_IN_COSTS_PAYMENT,
  REQUEST_PAY_ONE_TIME_PAYMENT,
  REQUEST_RENT_DETAILS,
  REQUEST_RENTER_STATUS,
  REQUEST_UPDATE_MODE_LINK_TOKEN,
  REQUEST_USER_API,
} from '@renter-hub/constants/actionTypes';
import RenterStatusModel from '@renter-hub/models/RenterStatus';
import FeaturePermissionModel from '@renter-hub/models/FeaturePermission';
import ConfigModel from '@renter-hub/models/Config';
import { MoveInCostsModel } from '@renter-hub/models/MoveInCosts';
import { formatTimestampToYMDString, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import StringConstants from '@common/constants/StringConstants';
import type {
  RequestCreateManualPaymentInterface,
  RequestInitiatePaymentInterface,
  RequestMoveInCostsPaymentInterface,
  RequestPayOneTimePaymentInterface,
} from '@renter-hub/models/RequestsTypes';

const renterHubProxy = StringConstants.API.RENTER_HUB.PROXY;
const renterHubApi = StringConstants.API.RENTER_HUB.API;
const renterHubApiV1 = renterHubProxy + renterHubApi;
const requestHeader = {
  'Content-Type': 'application/json',
};

type RequestResponse = {
  [x: number]: Record<string, unknown>;
};

export const getUserRenter = ({ requireStrongAuthentication }): RequestResponse => ({
  [RSAA]: {
    endpoint: `${renterHubApiV1}/get?pageWithSensitiveInformation=${requireStrongAuthentication}`,
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [{ type: REQUEST_USER_API.INIT }, { type: REQUEST_USER_API.SUCCESS }, { type: REQUEST_USER_API.FAIL }],
  },
});

export const requestRenterStatus = (encryptedInvite = ''): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getRenterStatus'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ encryptedInvite }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_RENTER_STATUS.INIT,
      {
        type: REQUEST_RENTER_STATUS.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json: any = {}) => ({
              renterStatus: RenterStatusModel(json.data),
              config: ConfigModel(json.config),
              httpStatus: json.httpStatus,
            }))
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_RENTER_STATUS.FAIL,
    ],
  },
});

export const requestRentDetailsFromInvite = (encryptedInvite: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getRentDetailsFromInvite'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ encryptedInvite }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_RENT_DETAILS.INIT,
      {
        type: REQUEST_RENT_DETAILS.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => {
              const { data } = json;
              const { firstDueDate, lastDueDate, moveInCosts } = data;

              return {
                ...data,
                firstDueDate: formatTimestampToYMDString(firstDueDate),
                lastDueDate: formatTimestampToYMDString(lastDueDate),
                moveInCosts: MoveInCostsModel(moveInCosts),
              };
            })
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_RENT_DETAILS.FAIL,
    ],
  },
});

export const requestInitiatePayment = (paymentDetails: RequestInitiatePaymentInterface): RequestResponse => {
  const { paymentDate } = paymentDetails;
  const newPaymentDetails = {
    ...paymentDetails,
    paymentDate: formatYMDStringToTimestamp(paymentDate),
  };
  return {
    [RSAA]: {
      endpoint: buildRenterPaymentsUrlV2('/createPayment'),
      method: POST,
      headers: requestHeader,
      body: JSON.stringify(newPaymentDetails),
      credentials: 'same-origin', // add cookies to requests of same origin
      types: [
        REQUEST_INITIATE_PAYMENT.INIT,
        {
          type: REQUEST_INITIATE_PAYMENT.SUCCESS,
          payload: (action, state, res) =>
            res
              .json()
              .then(checkStatus)
              .then((json) => json.data)
              .catch((json) => ({
                error: true,
                originalResponse: json,
              })),
        },
        REQUEST_INITIATE_PAYMENT.FAIL,
      ],
    },
  };
};

export const requestCreateManualPayment = (
  manualPaymentDetails: RequestCreateManualPaymentInterface,
): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createManualPayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(manualPaymentDetails),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_CREATE_MANUAL_PAYMENT.INIT,
      {
        type: REQUEST_CREATE_MANUAL_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_CREATE_MANUAL_PAYMENT.FAIL,
    ],
  },
});

export const requestCancelScheduledPayment = (subscriptionType: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/cancelPayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ type: subscriptionType }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_CANCEL_SCHEDULED_PAYMENT.INIT,
      {
        type: REQUEST_CANCEL_SCHEDULED_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_CANCEL_SCHEDULED_PAYMENT.FAIL,
    ],
  },
});

export const deletePaymentMethod = (encodedPaymentMethodId: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/deletePaymentMethod'),
    method: POST,
    headers: requestHeader,
    body: (state: Record<string, any>): string =>
      JSON.stringify({
        userId: state.user.userToken,
        authZone: 'zillow',
        encodedPaymentMethodId,
      }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_DELETE_PAYMENT_METHOD.INIT,
      {
        type: REQUEST_DELETE_PAYMENT_METHOD.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_DELETE_PAYMENT_METHOD.FAIL,
    ],
  },
});

export const requestPayOneTimePayment = (paymentInfo: RequestPayOneTimePaymentInterface): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/payOneTimePayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify(paymentInfo),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_PAY_ONE_TIME_PAYMENT.INIT,
      {
        type: REQUEST_PAY_ONE_TIME_PAYMENT.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_PAY_ONE_TIME_PAYMENT.FAIL,
    ],
  },
});

export const requestMoveInCostsPayment = (
  moveInCostsPaymentInfo: RequestMoveInCostsPaymentInterface,
): RequestResponse => {
  const { paymentDate } = moveInCostsPaymentInfo;
  const newMoveInCostsPaymentInfo = {
    ...moveInCostsPaymentInfo,
    paymentDate: formatYMDStringToTimestamp(paymentDate),
  };
  return {
    [RSAA]: {
      endpoint: buildRenterPaymentsUrlV2('/scheduleMoveInCosts'),
      method: POST,
      headers: requestHeader,
      body: JSON.stringify(newMoveInCostsPaymentInfo),
      credentials: 'same-origin', // add cookies to requests of same origin
      types: [
        REQUEST_MOVE_IN_COSTS_PAYMENT.INIT,
        {
          type: REQUEST_MOVE_IN_COSTS_PAYMENT.SUCCESS,
          payload: (action, state, res) =>
            res
              .json()
              .then(checkStatus)
              .then((json) => json.data)
              .catch((json) => ({
                error: true,
                originalResponse: json,
              })),
        },
        REQUEST_MOVE_IN_COSTS_PAYMENT.FAIL,
      ],
    },
  };
};

export const requestMarkAcknowledged = (actionId): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/markAcknowledged'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ actionId }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      REQUEST_MARK_ACKNOWLEDGED.INIT,
      {
        type: REQUEST_MARK_ACKNOWLEDGED.SUCCESS,
        payload: (action, state, res) =>
          res
            .json()
            .then(checkStatus)
            .then((json) => json.data)
            .catch((json) => ({
              error: true,
              originalResponse: json,
            })),
      },
      REQUEST_MARK_ACKNOWLEDGED.FAIL,
    ],
  },
});

export const requestUpdateModeLinkToken = (externalId?: string): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/createUpdateModeLinkToken'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      ...(externalId && { externalId }),
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.INIT,
        meta: { externalId },
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.SUCCESS,
        meta: { externalId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: ConfigModel(json.data),
          })),
      },
      {
        type: REQUEST_UPDATE_MODE_LINK_TOKEN.FAIL,
        meta: { externalId },
      },
    ],
  },
});

export const getFeaturePermissions = ({ propertyId }): RequestResponse => ({
  [RSAA]: {
    endpoint: buildRenterPaymentsUrlV2('/getFeaturePermissions'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: FeaturePermissionModel(json.data),
          })),
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

/**
 * Execute >1 requests in parallel
 * Use this when feature permissions may have changed, as well as renter status.
 */
export const getRachelDashboard =
  ({
    encryptedInvite = '',

    propertyId,
  }: {
    encryptedInvite?: string;
    propertyId?: string;
  }) =>
  async (dispatch) =>
    Promise.all([
      await dispatch(requestRenterStatus(encryptedInvite)),
      // await dispatch(getFeaturePermissions({ propertyId })),
    ]);
